import { useMemo } from 'react';
import { formatToUniversalDate } from 'utils/dates';
import type {
  EstimateKitShipDateProps,
  EstimatedShipDateResult,
} from 'hooks/useEstimateKitShipDate/types';
import {
  findLatestExpectedStockLot,
  calculateEstimatedProcessingDate,
  calculateFinalShipDate,
  getEstimatedShipDateToRender,
  getHasEstimatedShipDateChanged,
} from 'hooks/useEstimateKitShipDate/utils';

export * from 'hooks/useEstimateKitShipDate/types';
export * from 'hooks/useEstimateKitShipDate/utils';
export * from 'hooks/useEstimateKitShipDate/copy';

/**
 * Hook to estimate the ship date for a kit based on its request lines and processing requirements
 *
 * The estimation takes into account:
 * - Number of reels needed
 * - Number of shipment lines to process
 * - Number of merges required
 * - Number of splits required
 * - Expected dates of any stock lots
 *
 * @param props - {@link EstimateKitShipDateProps}
 * @returns {@link EstimatedShipDateResult}
 */
export const useEstimateKitShipDate = ({
  decoratedKitRequestLines,
  handlingRequestData,
  kitRequest,
}: EstimateKitShipDateProps): EstimatedShipDateResult =>
  useMemo(() => {
    if (!kitRequest)
      return {
        estimatedShipDate: null,
        estimatedProcessingDate: null,
        latestExpectedStockLot: null,
        estimatedShipDateToRender: null,
        hasEstimatedShipDateChanged: false,
      };
    const latestExpectedStockLot = findLatestExpectedStockLot(
      decoratedKitRequestLines
    );
    const estimatedProcessingDate = calculateEstimatedProcessingDate(
      decoratedKitRequestLines,
      handlingRequestData,
      kitRequest?.approvedAt
    );
    const estimatedShipDate = calculateFinalShipDate(
      decoratedKitRequestLines,
      estimatedProcessingDate,
      latestExpectedStockLot,
      kitRequest.shipPartial
    );

    const estimatedShipDateToRender = getEstimatedShipDateToRender({
      kitRequest,
      estimatedShipDate,
    });

    const hasEstimatedShipDateChanged = getHasEstimatedShipDateChanged({
      kitRequest,
      estimatedShipDate,
    });

    return {
      estimatedShipDate,
      estimatedProcessingDate: estimatedProcessingDate
        ? formatToUniversalDate(estimatedProcessingDate)
        : null,
      latestExpectedStockLot,
      estimatedShipDateToRender,
      hasEstimatedShipDateChanged,
    };
  }, [decoratedKitRequestLines, handlingRequestData, kitRequest]);
